import type { FlowSectionField } from "../form-builder/types";
import { useEditableField } from "./use-editable-field";

interface PreviewFieldProps {
	field: FlowSectionField;
	onLabelChange?: (label: string) => void;
	onPlaceholderChange?: (placeholder: string) => void;
	isDarkMode?: boolean;
}

// Placeholder hints for field labels based on field_key
const DEFAULT_FIELD_LABELS: Record<string, string> = {
	customer_name: "Enter label for Name",
	customer_email: "Enter label for Email",
	customer_phone: "Enter label for Phone",
	customer_avatar_url: "Enter label for Photo",
	customer_job_title: "Enter label for Job title",
	customer_company_name: "Enter label for Company",
	customer_company_website: "Enter label for Website",
	customer_company_logo_url: "Enter label for Company logo",
	feedback_text: "Enter label for Feedback",
};

// Icons for file upload fields based on field_key
function FileUploadIcon({ fieldKey, isDarkMode }: { fieldKey: string; isDarkMode: boolean }) {
	const iconClass = `w-8 h-8 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`;

	if (fieldKey === "customer_avatar_url") {
		return (
			<svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
				/>
			</svg>
		);
	}

	if (fieldKey === "customer_company_logo_url") {
		return (
			<svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
				/>
			</svg>
		);
	}

	// Generic upload icon
	return (
		<svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
		</svg>
	);
}

export default function PreviewField({ field, onLabelChange, onPlaceholderChange, isDarkMode = false }: PreviewFieldProps) {
	const labelEditable = useEditableField(field.label, onLabelChange);
	const placeholderEditable = useEditableField(field.placeholder || "", onPlaceholderChange);

	const isFileField = field.field_type === "file";
	const isTextareaField = field.field_type === "textarea";

	const placeholderLabel =
		DEFAULT_FIELD_LABELS[field.field_key] ||
		`Enter label for ${field.field_key
			.replace(/^customer_/, "")
			.replace(/^company_/, "")
			.replace(/_url$/, "")
			.replace(/_/g, " ")
			.replace(/^\w/, (c) => c.toUpperCase())}`;

	// Editable label element
	const labelElement = (
		<label className={`block font-medium text-sm mb-2 ${isDarkMode ? "text-white" : "text-primary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
			<span
				className={`relative inline-flex items-baseline whitespace-nowrap rounded-sm transition-shadow cursor-text ${
					isDarkMode
						? "hover:ring-2 hover:ring-gray-600 focus-within:ring-2 focus-within:ring-brand/50"
						: "hover:ring-2 hover:ring-gray-300 focus-within:ring-2 focus-within:ring-brand/50"
				}`}
			>
				{/* Invisible span to reserve placeholder width */}
				{labelEditable.isEmpty && <span className="invisible whitespace-nowrap">{placeholderLabel}</span>}
				<span
					ref={labelEditable.ref}
					contentEditable
					suppressContentEditableWarning
					className={`${labelEditable.isEmpty ? "absolute inset-0" : ""} outline-none cursor-text ${
						isDarkMode
							? labelEditable.isEmpty ? "text-transparent caret-white" : ""
							: labelEditable.isEmpty ? "text-transparent caret-black" : ""
					}`}
					{...labelEditable.handlers}
				/>
				{labelEditable.isEmpty && (
					<span className={`absolute left-0 pointer-events-none whitespace-nowrap ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}>{placeholderLabel}</span>
				)}
			</span>
			{field.is_required && <span className="text-red-500"> *</span>}
		</label>
	);

	if (isFileField) {
		const isAvatar = field.field_key === "customer_avatar_url";
		return (
			<div>
				{labelElement}
				<div
					className={`${isAvatar ? "w-32.5 aspect-square" : "h-20"} border border-dashed rounded-lg flex items-center justify-center cursor-not-allowed ${
						isDarkMode ? "border-gray-700 bg-gray-900" : "border-gray-300 bg-gray-50"
					}`}
				>
					<FileUploadIcon fieldKey={field.field_key} isDarkMode={isDarkMode} />
				</div>
			</div>
		);
	}

	if (isTextareaField) {
		return (
			<div>
				{labelElement}
				<div className="relative" style={{ minHeight: "140px" }}>
					<div
						ref={placeholderEditable.ref}
						contentEditable
						suppressContentEditableWarning
						className={`w-full border rounded-lg shadow-sm py-3 px-5 text-sm font-light outline-none rounded-sm transition-shadow cursor-text ${
							isDarkMode
								? `bg-gray-900 border-gray-800 hover:ring-2 hover:ring-gray-600 focus:ring-2 focus:ring-brand/50 ${placeholderEditable.isEmpty ? "text-transparent caret-white" : "text-gray-500"}`
								: `bg-white border-gray-200 hover:ring-2 hover:ring-gray-300 focus:ring-2 focus:ring-brand/50 ${placeholderEditable.isEmpty ? "text-transparent caret-black" : "text-gray-400"}`
						}`}
						style={{ minHeight: "140px", fontFamily: "var(--font-body, inherit)" }}
						{...placeholderEditable.handlers}
					/>
					{placeholderEditable.isEmpty && (
						<span
							className={`absolute top-3 left-5 text-sm font-light pointer-events-none ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
							style={{ fontFamily: "var(--font-body, inherit)" }}
						>
							Placeholder text
						</span>
					)}
				</div>
			</div>
		);
	}

	// Default: text/email/tel/url input preview
	return (
		<div>
			{labelElement}
			<div className="relative">
				<div
					ref={placeholderEditable.ref}
					contentEditable
					suppressContentEditableWarning
					className={`w-full border rounded-lg shadow-sm py-2 px-4 text-sm font-light outline-none rounded-sm transition-shadow cursor-text ${
						isDarkMode
							? `bg-gray-900 border-gray-800 hover:ring-2 hover:ring-gray-600 focus:ring-2 focus:ring-brand/50 ${placeholderEditable.isEmpty ? "text-transparent caret-white" : "text-gray-500"}`
							: `bg-white border-gray-200 hover:ring-2 hover:ring-gray-300 focus:ring-2 focus:ring-brand/50 ${placeholderEditable.isEmpty ? "text-transparent caret-black" : "text-gray-400"}`
					}`}
					style={{ fontFamily: "var(--font-body, inherit)" }}
					{...placeholderEditable.handlers}
				/>
				{placeholderEditable.isEmpty && (
					<span
						className={`absolute top-2 left-4 text-sm font-light pointer-events-none ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
						style={{ fontFamily: "var(--font-body, inherit)" }}
					>
						Placeholder text
					</span>
				)}
			</div>
		</div>
	);
}
